import { useState, useMemo, useEffect } from 'react';
import { Cell } from '@/components/Cell';
import { Empty } from '@/components/Empty';
import { Spinner } from '@/components/Spinner';
import {
  extractOptionBase,
  extractOptionExpiry,
  extractOptionSide,
  fmtOptionExpiry,
} from '@/lib/optionSymbolFilters';

/** Котировка опциона из скрапа. */
export interface ScrapeQuote {
  symbol: string;
  bid: number;
  ask: number;
  taken_at: string;
}

interface ScrapeQuotesTableProps {
  quotes: ScrapeQuote[];
  isLoading: boolean;
  underlyingPrices?: Array<{ base: string; spot_price: number }>;
}

/** Сколько котировок группы показываем за раз (в скрапе бывают тысячи строк). */
const GROUP_PAGE_SIZE = 100;

/** Форматирует spot-цену базового актива. */
function fmtSpot(n: number): string {
  if (!Number.isFinite(n)) return '—';
  if (n >= 1000) return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
  if (n >= 1)    return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
  return n.toLocaleString('en-US', { maximumFractionDigits: 6 });
}

/**
 * Таблица котировок скрапа с фильтрами: поиск, Call/Put, дата экспирации,
 * группировка по базовому активу — аналогично «Быстрому добавлению» в Epsilon.
 */
export function ScrapeQuotesTable({ quotes, isLoading, underlyingPrices }: ScrapeQuotesTableProps): JSX.Element {
  const [search, setSearch] = useState('');
  const [filterSide, setFilterSide] = useState<'all' | 'C' | 'P'>('all');
  const [filterExpiry, setFilterExpiry] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  // Сколько строк показано в каждой раскрытой группе (по умолчанию GROUP_PAGE_SIZE).
  const [groupLimits, setGroupLimits] = useState<Record<string, number>>({});

  // При смене фильтров или самого скрапа начинаем снова с первых GROUP_PAGE_SIZE строк.
  useEffect(() => {
    setGroupLimits({});
  }, [quotes, search, filterSide, filterExpiry]);

  const spotMap = useMemo(() => {
    const m = new Map<string, number>();
    for (const u of underlyingPrices ?? []) m.set(u.base, u.spot_price);
    return m;
  }, [underlyingPrices]);

  const expiryDates = useMemo(() => {
    const dates = new Set<string>();
    for (const q of quotes) {
      const e = extractOptionExpiry(q.symbol);
      if (e) dates.add(e);
    }
    return [...dates].sort();
  }, [quotes]);

  const groups = useMemo(() => {
    const q = search.trim().toUpperCase();
    const filtered = quotes.filter((item) => {
      if (q && !item.symbol.toUpperCase().includes(q)) return false;
      if (filterSide !== 'all' && extractOptionSide(item.symbol) !== filterSide) return false;
      if (filterExpiry && extractOptionExpiry(item.symbol) !== filterExpiry) return false;
      return true;
    });
    const map = new Map<string, ScrapeQuote[]>();
    for (const item of filtered) {
      const base = extractOptionBase(item.symbol);
      if (!map.has(base)) map.set(base, []);
      map.get(base)!.push(item);
    }
    return map;
  }, [quotes, search, filterSide, filterExpiry]);

  const showMoreInGroup = (base: string): void => {
    setGroupLimits((prev) => ({ ...prev, [base]: (prev[base] ?? GROUP_PAGE_SIZE) + GROUP_PAGE_SIZE }));
  };

  const toggleGroup = (base: string): void => {
    setGroupLimits((prev) => {
      if (!(base in prev)) return prev;
      const next = { ...prev };
      delete next[base];
      return next;
    });
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(base)) next.delete(base);
      else next.add(base);
      return next;
    });
  };

  if (isLoading) return <Spinner />;

  if (quotes.length === 0) {
    return <Empty icon="📊" title="Нет данных" hint="Попробуйте выбрать другой скрап." />;
  }

  return (
    <div>
      {/* Строка поиска */}
      <div
        style={{
          padding: 'var(--space-sm) var(--space-lg)',
          borderBottom: '0.5px solid var(--tg-separator)',
        }}
      >
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Поиск по символу…"
          autoCapitalize="characters"
          style={{
            width: '100%',
            background: 'var(--tg-secondary-bg)',
            border: 'none',
            outline: 'none',
            padding: 'var(--space-sm) var(--space-md)',
            borderRadius: 'var(--radius-sm)',
            fontSize: 'var(--font-md)',
            color: 'var(--tg-text)',
            boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Фильтры: Call / Put / All + дата экспирации */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-sm)',
          padding: 'var(--space-sm) var(--space-lg)',
          borderBottom: '0.5px solid var(--tg-separator)',
          flexWrap: 'wrap',
        }}
      >
        {(['all', 'C', 'P'] as const).map((side) => (
          <button
            key={side}
            type="button"
            onClick={() => setFilterSide(side)}
            style={{
              padding: '4px 12px',
              borderRadius: 'var(--radius-sm)',
              border: '0.5px solid var(--tg-separator)',
              background: filterSide === side ? 'var(--tg-button)' : 'var(--tg-secondary-bg)',
              color: filterSide === side ? 'var(--tg-button-text)' : 'var(--tg-text)',
              fontSize: 'var(--font-sm)',
              cursor: 'pointer',
              fontWeight: filterSide === side ? 600 : 400,
            }}
          >
            {side === 'all' ? 'Все' : side === 'C' ? 'Call' : 'Put'}
          </button>
        ))}

        <select
          value={filterExpiry}
          onChange={(e) => setFilterExpiry(e.target.value)}
          style={{
            flex: 1,
            minWidth: 120,
            background: filterExpiry ? 'var(--tg-button)' : 'var(--tg-secondary-bg)',
            color: filterExpiry ? 'var(--tg-button-text)' : 'var(--tg-text)',
            border: '0.5px solid var(--tg-separator)',
            borderRadius: 'var(--radius-sm)',
            padding: '4px var(--space-sm)',
            fontSize: 'var(--font-sm)',
            cursor: 'pointer',
          }}
        >
          <option value="">Все даты</option>
          {expiryDates.map((d) => (
            <option key={d} value={d}>{fmtOptionExpiry(d)}</option>
          ))}
        </select>
      </div>

      {/* Результаты */}
      {groups.size === 0 ? (
        <div
          style={{
            padding: 'var(--space-lg)',
            color: 'var(--tg-hint)',
            fontSize: 'var(--font-sm)',
            textAlign: 'center',
          }}
        >
          Нет совпадений
        </div>
      ) : (
        Array.from(groups.entries()).map(([base, items]) => {
          const isOpen = expandedGroups.has(base);
          const spot = spotMap.get(base);
          const limit = groupLimits[base] ?? GROUP_PAGE_SIZE;
          const hiddenCount = Math.max(0, items.length - limit);
          return (
            <div key={base}>
              {/* Заголовок группы */}
              <button
                type="button"
                onClick={() => toggleGroup(base)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: 'var(--space-sm) var(--space-lg)',
                  background: 'var(--tg-secondary-bg)',
                  border: 'none',
                  borderBottom: '0.5px solid var(--tg-separator)',
                  cursor: 'pointer',
                  color: 'var(--tg-text)',
                  fontSize: 'var(--font-md)',
                  fontWeight: 600,
                  textAlign: 'left',
                }}
              >
                <span>{base}</span>
                <span style={{ display: 'flex', gap: 'var(--space-sm)', alignItems: 'center', fontSize: 'var(--font-sm)', color: 'var(--tg-hint)' }}>
                  {spot != null && <span style={{ fontWeight: 500 }}>{fmtSpot(spot)}</span>}
                  <span>{items.length} {isOpen ? '▲' : '▼'}</span>
                </span>
              </button>

              {/* Котировки группы */}
              {isOpen && items.slice(0, limit).map((q) => (
                <Cell
                  key={q.symbol}
                  title={q.symbol}
                  subtitle={`bid ${q.bid} · ask ${q.ask}`}
                />
              ))}
              {isOpen && hiddenCount > 0 && (
                <button
                  type="button"
                  onClick={() => showMoreInGroup(base)}
                  style={{
                    width: '100%',
                    padding: 'var(--space-sm) var(--space-lg)',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: '0.5px solid var(--tg-separator)',
                    color: 'var(--tg-link, var(--tg-button))',
                    fontSize: 'var(--font-sm)',
                    cursor: 'pointer',
                    minHeight: 'var(--tap-target)',
                  }}
                >
                  {`Показать ещё (${hiddenCount})`}
                </button>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
